import { RawModel } from "@/3d/rawModel/RawModel";
import { FileLoader } from "three/src/loaders/FileLoader";
import { create } from "zustand";
import {
    createMeshConfig,
    MeshConfig,
    saveMeshConfigToLocalStore,
} from "@/3d/modelConfig/MeshConfig";
import { MeshStats } from "@/3d/modelConfig/MeshStats";
import { Element } from "@/3d/element/Element";
import { v4 as uuidv4 } from "uuid";
import { Area } from "@/3d/area/Area";
import { saveModelConfigToLocalStore, ModelConfig } from "@/3d/modelConfig/ModelConfig";
import {
    addElementInstance,
    createModelInstance,
    ModelInstance,
    resizeModelInstance,
} from "@/3d/modelInstance/ModelInstance";
import { MaterialSlot } from "@/3d/material/MaterialSlot";
import { MaterialResources } from "./MaterialResources";
import { MeshGroup } from "@/3d/meshGroup/MeshGroup";
import { createGroupInstances } from "@/3d/modelInstance/GroupInstance";
import { loadModel } from "@/api/model";
import { loadModelMaterials } from "@/api/model";
import { getDefaultModelConfig } from "@/3d/modelConfig/getDefaultModelConfig";

export type VertexSelectionData = {
    meshName: string;
    index: number;
};

type EditorState = {
    modelPath: string | undefined;
    modelFileName: string | undefined;
    model: RawModel | undefined;
    renderVertices: boolean;
    selectedMesh: string | undefined;
    selectedVertices: VertexSelectionData[];
    meshConfig: Record<string, MeshConfig>;
    meshStats: Record<string, MeshStats>;
    hiddenMeshes: string[];
    resizeTest: boolean;
    resizeTargetDimensions: { x: number; y: number; z: number };
    currentMeshMaterialName: string | undefined;
    setCurrentMeshMaterialName: (materialName: string | undefined) => void;

    materialSlots: Record<string, MaterialSlot>;
    materialMapping: Record<string, string>;

    elements: Record<string, Element>;
    elementInstanceTest: boolean;

    currentElement: Element | undefined;

    meshAreas: Record<string, Area>;

    modelInstance: ModelInstance | undefined;

    meshGroups: Record<string, MeshGroup>;
    applyConfig: (modelConfig: ModelConfig, materials: Record<string, MaterialResources>) => void;
    getConfig: () => ModelConfig;
    createElement: () => void;
    updateCurrentElement: (element: Partial<Element> & Pick<Element, "id">) => void;
    setCurrentElement: (elementId: string) => void;

    setMaterialSlots: (materialSlots: Record<string, MaterialSlot>) => void;
    setMaterialMapping: (materialMapping: Record<string, string>) => void;

    loadModel: (
        path: string,
        fileName: string,
        materials: Record<string, MaterialResources>
    ) => void;
    setModelWithConfig: (
        modelPath: string,
        modelFileName: string,
        model: RawModel,
        modelConfig: ModelConfig,
        materials: Record<string, MaterialResources>
    ) => void;
    setRenderVertices: (renderVertices: boolean) => void;
    setSelectedMesh: (selectedMesh: string | undefined) => void;
    setSelectedVertices: (selectedVertices: VertexSelectionData[]) => void;
    setMeshConfig: (meshName: string, meshConfig: MeshConfig) => void;
    setHiddenMeshes: (hiddenMeshes: string[]) => void;
    setResizeTest: (resizeTest: boolean) => void;
    setResizeTargetDimensions: (resizeTargetDimensions: {
        x: number;
        y: number;
        z: number;
    }) => void;
    initMeshAreas: () => void;
    updateArea: (areaName: string, area: Partial<Area>) => void;

    setElementInstanceTest: (elementInstanceTest: boolean) => void;
    clearElementInstances: () => void;
    createElementInstance: (elementId: string) => void;

    saveModelConfig: () => void;

    setMeshGroups: (meshGroups: Record<string, MeshGroup>) => void;
};

export const useEditorState = create<EditorState>((set, get) => ({
    modelPath: undefined,
    modelFileName: undefined,
    model: undefined,
    renderVertices: true,
    selectedMesh: undefined,
    selectedVertices: [],
    meshConfig: {},
    meshStats: {},
    hiddenMeshes: [],
    resizeTest: false,
    resizeTargetDimensions: { x: 1, y: 1, z: 1 },
    currentMeshMaterialName: undefined,

    materialSlots: {},
    materialMapping: {},

    elements: {},
    elementInstances: [],
    currentElement: undefined,
    meshAreas: {},
    modelInstance: undefined,
    elementInstanceTest: false,

    meshGroups: {},

    loadModel: async (
        path: string,
        fileName: string,
        materials: Record<string, MaterialResources>
    ) => {
        const loader = new FileLoader();
        const raw = await loadModel(path, loader);
        if (!raw) {
            console.error("raw model not found");
            return undefined;
        }

        await loadModelMaterials(raw, path, loader);

        set({
            model: raw,
            modelPath: path,
            modelFileName: fileName,
        });

        get().applyConfig(getDefaultModelConfig(raw), materials);
    },

    setModelWithConfig: (
        modelPath: string,
        modelFileName: string,
        model: RawModel,
        modelConfig: ModelConfig,
        materials: Record<string, MaterialResources>
    ) => {
        set({
            model,
            modelPath,
            modelFileName,
        });

        get().applyConfig(modelConfig, materials);
    },

    applyConfig: async (modelConfig: ModelConfig, materials: Record<string, MaterialResources>) => {
        const raw = get().model;

        if (!raw || !materials) {
            return;
        }

        const meshConfig = modelConfig.meshConfig;

        const meshStats: Record<string, MeshStats> = {};

        raw.meshes.forEach((mesh) => {
            const config = meshConfig[mesh.name] ?? createMeshConfig(mesh);
            if (!meshConfig[mesh.name]) {
                meshConfig[mesh.name] = config;
            }

            meshStats[mesh.name] = new MeshStats(mesh, config);
        });

        const modelInstance = createModelInstance(
            raw,
            modelConfig,
            meshStats,
            materials,
            raw.boundingRect
        );

        set({
            meshConfig,
            meshStats,
            resizeTargetDimensions: {
                x: raw.boundingRect.sizeX,
                y: raw.boundingRect.sizeY,
                z: raw.boundingRect.sizeZ,
            },
            elements: modelConfig.elements,
            hiddenMeshes: modelConfig.hiddenMeshes,
            meshAreas: Object.fromEntries(
                Object.entries(modelConfig.meshAreas).map(([key, value]) => [
                    key,
                    Area.fromLike(value),
                ])
            ),
            modelInstance,
            materialSlots: modelConfig.materialSlots,
            materialMapping: modelConfig.materialMapping,
            meshGroups: modelConfig.meshGroups,
        });

        get().initMeshAreas();
    },

    setRenderVertices: (renderVertices: boolean) => {
        set({ renderVertices });
    },

    setSelectedMesh: (selectedMesh: string | undefined) => {
        set({ selectedMesh });
    },

    setSelectedVertices: (selectedVertices: VertexSelectionData[]) => {
        set({ selectedVertices });
    },

    setMeshConfig: (meshName: string, meshConfig: MeshConfig) => {
        const currentMeshConfig = get().meshConfig;
        const newMeshConfig = { ...currentMeshConfig, [meshName]: meshConfig };

        const mesh = get().model?.meshes.find((m) => m.name === meshName);
        if (!mesh) {
            console.error(`Mesh ${meshName} not found`);
            return;
        }

        const currentMeshStats = get().meshStats;
        const newMeshStats = {
            ...currentMeshStats,
            [meshName]: new MeshStats(mesh, meshConfig),
        };

        set({ meshConfig: newMeshConfig, meshStats: newMeshStats });
        saveMeshConfigToLocalStore(get().modelPath ?? "default", newMeshConfig);
    },

    setHiddenMeshes: (hiddenMeshes: string[]) => {
        set({ hiddenMeshes });
    },

    setResizeTest: (resizeTest: boolean) => {
        const modelInstance = get().modelInstance;
        if (!modelInstance) {
            return;
        }
        const resizedModelInstance = resizeModelInstance(
            modelInstance,
            get().meshStats,
            get().resizeTest
                ? get().resizeTargetDimensions
                : {
                      x: modelInstance.raw.boundingRect.sizeX,
                      y: modelInstance.raw.boundingRect.sizeY,
                      z: modelInstance.raw.boundingRect.sizeZ,
                  }
        );

        set({ resizeTest, modelInstance: resizedModelInstance });
    },

    setResizeTargetDimensions: (resizeTargetDimensions: { x: number; y: number; z: number }) => {
        const modelInstance = get().modelInstance;
        if (!modelInstance) {
            return;
        }

        const resizedModelInstance = resizeModelInstance(
            modelInstance,
            get().meshStats,
            get().resizeTest
                ? resizeTargetDimensions
                : {
                      x: modelInstance.raw.boundingRect.sizeX,
                      y: modelInstance.raw.boundingRect.sizeY,
                      z: modelInstance.raw.boundingRect.sizeZ,
                  }
        );

        set({ resizeTargetDimensions, modelInstance: resizedModelInstance });
    },

    createElement: () => {
        const id = uuidv4();
        const newElement: Element = {
            id,
            name: `Element-${id}`,
            meshName: "",
            parentAreaName: "",
            areaConfig: {
                minSize: 0.1,
                maxSize: 0.2,
                type: "1axis",
                minParentStartOffset: 0.1,
                minStartOffset: 0,
                minEndOffset: 0,
            },
        };

        set({ elements: { ...get().elements, [id]: newElement }, currentElement: newElement });
    },

    updateCurrentElement: (element: Partial<Element> & Pick<Element, "id">) => {
        const currentElement = get().currentElement;
        if (!currentElement) {
            return;
        }

        const updatedElement = { ...currentElement, ...element };
        set({
            currentElement: updatedElement,
            elements: { ...get().elements, [updatedElement.id]: updatedElement },
        });
    },

    setCurrentElement: (elementId: string) => {
        set({ currentElement: get().elements[elementId] });
    },

    initMeshAreas: () => {
        const config = get().meshConfig;
        const model = get().model;
        if (!config || !model) {
            return;
        }

        const areas = get().meshAreas;
        const newAreas = Object.values(config).filter((m) => m.isArea && !areas[m.meshName]);

        newAreas.forEach((area) => {
            const newArea = new Area(
                { axis: "y", type: "1axis" },
                model.meshes.find((m) => m.name === area.meshName)!.boundingRect
            );

            areas[area.meshName] = newArea;
        });

        set({ meshAreas: areas });
    },

    updateArea: (areaName: string, data: Partial<Area>) => {
        const currentAreas = get().meshAreas;
        const area = currentAreas[areaName];
        if (data.layout) {
            area.layout = data.layout;
        }

        if (data.config) {
            area.config = data.config;
        }

        const updatedAreas = {
            ...currentAreas,
            [areaName]: area,
        };

        set({ meshAreas: updatedAreas });
    },

    setElementInstanceTest: (elementInstanceTest: boolean) => {
        set({ elementInstanceTest });
    },

    clearElementInstances: () => {
        const modelInstance = get().modelInstance;
        if (!modelInstance) {
            return;
        }

        const updatedModelInstance = { ...modelInstance, elementInstances: [] };
        set({ modelInstance: updatedModelInstance });
    },

    createElementInstance: (elementId: string) => {
        const modelInstance = get().modelInstance;
        const element = get().elements[elementId];

        if (!modelInstance || !element) {
            return;
        }

        const updatedModelInstance = addElementInstance(modelInstance, element);
        set({ modelInstance: updatedModelInstance });
    },

    saveModelConfig: () => {
        const modelPath = get().modelPath;
        if (!modelPath) {
            console.error("Model path not found");
            return;
        }

        saveModelConfigToLocalStore(modelPath, {
            v: "1.0.0",
            elements: get().elements,
            hiddenMeshes: get().hiddenMeshes,
            meshAreas: get().meshAreas,
            meshConfig: get().meshConfig,
            materialSlots: get().materialSlots,
            materialMapping: get().materialMapping,
            meshGroups: get().meshGroups,
        });
    },

    setMaterialSlots: (materialSlots: Record<string, MaterialSlot>) => {
        set({ materialSlots });
    },

    setMaterialMapping: (materialMapping: Record<string, string>) => {
        set({ materialMapping });
    },

    setMeshGroups: (meshGroups: Record<string, MeshGroup>) => {
        set({ meshGroups });

        const modelInstance = get().modelInstance;
        if (!modelInstance) {
            return;
        }

        const newConfig = {
            ...modelInstance.config,
            meshGroups,
        };

        const newGroupInstances = createGroupInstances(
            newConfig,
            (meshName) =>
                new MeshStats(
                    modelInstance.raw.meshes.find((m) => m.name === meshName)!,
                    modelInstance.config.meshConfig[meshName]
                ),
            modelInstance.raw,
            modelInstance.box
        );

        const updatedModelInstance = {
            ...modelInstance,
            groups: newGroupInstances,
            config: newConfig,
        };
        set({ modelInstance: updatedModelInstance });
    },

    getConfig: (): ModelConfig => {
        return {
            v: "1.0.0",
            elements: get().elements,
            hiddenMeshes: get().hiddenMeshes,
            meshAreas: get().meshAreas,
            meshConfig: get().meshConfig,
            materialSlots: get().materialSlots,
            materialMapping: get().materialMapping,
            meshGroups: get().meshGroups,
        };
    },

    setCurrentMeshMaterialName: (materialName: string | undefined) => {
        set({ currentMeshMaterialName: materialName });
    },
}));
